import Template from "../app/Template";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { useSearchParams } from "react-router-dom";
import Button from "../layout/Button";
import { CheckBadgeIcon } from "@heroicons/react/24/solid";
import Dropdown from "../forms/Dropdown";
import SectionHeader from "../layout/SectionHeader";
import { useEffect, useState } from "react";
import { getCategoryOptions } from "../../slices/category";
import TextInputSubmitOnEnterLeadingIcon from "../forms/TextInputSubmitOnEnterLeadingIcon";
import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import Spinner from "../layout/Spinner";
import ExploreTopicsAndCollections from "../layout/ExploreTopicsAndCollections";
import { getEventLocations, getEvents } from "../../slices/content";
import { Event } from "../../types/content";
import EventCard from "../layout/EventCard";
import { withFilters } from "../../utils/searchParams";


export default function Events() {
  const dispatch = useAppDispatch();

  // Get query params
  const [searchParams, setSearchParams] = useSearchParams();
  const querySearch = searchParams.get("query");
  const queryCategory = searchParams.get("category");
  const queryTag = searchParams.get("tag");
  const queryAudience = searchParams.get("audience");
  const queryLocation = searchParams.get("location");
  const queryPageNumber = searchParams.get("page") === null
    ? 1
    : parseInt(searchParams.get("page") as string);

  useEffect(() => {
    dispatch(getEvents({
      pageNumber: queryPageNumber,
      query: querySearch,
      category: queryCategory,
      tag: queryTag,
      audience: queryAudience,
      location: queryLocation,
      type: null, // Not used in this context
      startDate: null, // Not used in this context
      endDate: null // Not used in this context
    }));
  }, [
    queryPageNumber,
    querySearch,
    queryCategory,
    queryTag,
    queryAudience,
    queryLocation
  ]);

  useEffect(() => {
    dispatch(getCategoryOptions())
    dispatch(getEventLocations())
  }, []);

  // Get app level state from redux store
  const { user, loading: authLoading } = useAppSelector((state) => state.auth);
  const {
    events: { data: eventsData, loading: eventsLoading, waiting: eventsWaiting, pagination: eventsPagination, options },
  } = useAppSelector((state) => state.content);
  const {
    options: { data: categoryOptionsData, loading: categoryOptionsLoading },
  } = useAppSelector((state) => state.category);

  // Component level state. Seeded from the URL so a shared /events?query=...
  // link renders with its keyword visible in the box rather than an empty one.
  const [searchState, setSearchState] = useState(querySearch ?? "");

  // Re-sync when the URL changes without a keystroke -- browser back/forward.
  useEffect(() => {
    setSearchState(querySearch ?? "");
  }, [querySearch]);

  const applyFilters = (changes: Record<string, string | null>) =>
    setSearchParams(withFilters(searchParams, changes));

  // The API now returns past events as well as upcoming ones — it used to filter
  // them out server-side, which made the page go blank the moment the last event
  // started. Split them here so upcoming stays the headline and past events are
  // still reachable.
  const upcomingEvents = eventsData.filter((e: Event) => e.is_upcoming !== false);
  const pastEvents = eventsData.filter((e: Event) => e.is_upcoming === false);

  const renderEventCard = (event: Event) => (
    <EventCard
      key={event.id}
      objectId={event.id}
      objectType="Event"
      image={event.images[0] || "/img/graphics/hero/landing-hero.png"}
      title={event.title}
      host={event.host || ""}
      city={event?.city?.name || null}
      cityDetails={event?.city_details || null}
      startDate={event.start_date}
      endDate={event.end_date}
      primaryTags={event.tags}
      href={event.url}
      user={user}
      bgColour="dark-beige"
      rating={event.rating}
      reviews={event.reviews}
      price={event.price}
      synopsis={event.synopsis}
    />
  );

  // Constant variables
  const dropdownEventTagCategory: Array<{ label: string; value: string }> = categoryOptionsData.eventtagcategory === undefined ? [] : categoryOptionsData.eventtagcategory.map((x: any) => ({ label: x.name, value: x.name }));
  const dropdownEventTag: Array<{ label: string; value: string }> = categoryOptionsData.eventtag === undefined ? [] : queryCategory === null ? categoryOptionsData.eventtag.map((x: any) => ({ label: x.name, value: x.name })) : categoryOptionsData.eventtag.filter((x: any) => x.category?.name === queryCategory).map((x: any) => ({ label: x.name, value: x.name }));
  const dropdownAudiences: Array<{ label: string; value: string }> = categoryOptionsData.audience === undefined ? [] : categoryOptionsData.audience.map((x: any) => ({ label: x.name, value: x.name }));
  const dropdownLocations: Array<{ label: string; value: string }> = options.cities === undefined ? [] : options.cities.map((x: any) => ({ label: x.name, value: x.name }));

  return (
    <Template navbarMode="solid">

      <div className="grid grid-col-1 gap-y-20 mb-20">
        {/* Hero */}
        <div>
          <div
            className="mt-20 w-full bg-dark-blue py-12 md:py-20 relative"
          >
            <div className="grid grid-cols-1 gap-y-4 items-center justify-center">
              <h1 className="text-off-white text-3xl md:text-5xl lg:text-6xl font-bold mx-auto">Events</h1>

              {/* Keyword search */}
              <TextInputSubmitOnEnterLeadingIcon
                id="search-events"
                placeholder="Search events"
                value={searchState}
                icon={
                  <MagnifyingGlassIcon
                    aria-hidden="true"
                    className="pointer-events-none col-start-1 row-start-1 ml-3 size-5 self-center text-dark-grey sm:size-4"
                  />
                }
                widthClass="w-4/5 md:w-1/2 mx-auto"
                onChange={(e) => setSearchState(e.target.value)}
                onClick={() => {
                  // An empty box clears the keyword rather than doing nothing.
                  applyFilters({ query: searchState.trim() || null });
                }}
                loading={false}
                shadow="md"
              />
            </div>

            {/* Bubbles */}
            <img
              src="/img/graphics/bubbles/bubble-top-green.svg"
              alt="Bubble"
              className="absolute w-32 md:w-44 object-cover -bottom-10 md:-bottom-12 right-16 z-0"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex justify-center w-full gap-8">
          <div className="grid grid-cols-1 md:grid-cols-10 gap-x-8 w-4/5">

            {/* Filters */}
            <div className="col-span-2 grid grid-cols-1 h-fit gap-4 align-start">
              <h2 className="font-bold text-dark-grey">Refine search</h2>

              {/* Event Tag Category */}
              <Dropdown
                id="event-tag-category-dropdown"
                className=""
                showLabel={false}
                required={false}
                onChange={(e) => applyFilters({ category: e.target.value })}
                value={queryCategory || ""}
              >
                <option key="" value="">
                  All Types
                </option>
                {dropdownEventTagCategory.map((opt, idx) => (
                  <option key={`${idx}-${opt.value}`} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Dropdown>

              {/* Event Tag */}
              <Dropdown
                id="event-tag-dropdown"
                className=""
                showLabel={false}
                required={false}
                onChange={(e) => applyFilters({ tag: e.target.value })}
                value={queryTag || ""}
              >
                <option key="" value="">
                  All Tags
                </option>
                {dropdownEventTag.map((opt, idx) => (
                  <option key={`${idx}-${opt.value}`} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Dropdown>

              {/* Audience */}
              <Dropdown
                id="audience-dropdown"
                className=""
                showLabel={false}
                required={false}
                onChange={(e) => applyFilters({ audience: e.target.value })}
                value={queryAudience || ""}
              >
                <option key="" value="">
                  All Audiences
                </option>
                {dropdownAudiences.map((opt, idx) => (
                  <option key={`${idx}-${opt.value}`} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Dropdown>

              {/* Location */}
              <Dropdown
                id="location-dropdown"
                className=""
                showLabel={false}
                required={false}
                onChange={(e) => applyFilters({ location: e.target.value })}
                value={queryLocation || ""}
              >
                <option key="" value="">
                  All Locations
                </option>
                {dropdownLocations.map((opt, idx) => (
                  <option key={`${idx}-${opt.value}`} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Dropdown>

            </div>

            {/* Feed */}
            <div className="col-span-8 grid grid-cols-1 gap-8">
              {eventsLoading ? (
                <div className="w-full flex justify-center py-12"><Spinner size={8} /></div>
              ) : eventsData.length === 0 ? (
                // An explicit empty state. Previously an empty list rendered as
                // blank space, so "no events match these filters" was
                // indistinguishable from the page being broken.
                <div className="py-12 text-center">
                  <p className="text-dark-grey text-lg font-semibold mb-2">
                    {querySearch ? `No events match "${querySearch}"` : "No events to show"}
                  </p>
                  <p className="text-dark-grey text-sm">
                    {querySearch
                      ? "Try a different word, clear the filters on the left, or check back soon."
                      : "Try clearing the filters on the left, or check back soon."}
                  </p>
                </div>
              ) : (
                <>
                  {upcomingEvents.length > 0 && (
                    <section>
                      <h2 className="font-bold text-dark-grey text-xl mb-4">Upcoming events</h2>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                        {upcomingEvents.map(renderEventCard)}
                      </div>
                    </section>
                  )}

                  {pastEvents.length > 0 && (
                    <section>
                      <h2 className="font-bold text-dark-grey text-xl mb-4">Past events</h2>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4 opacity-75">
                        {pastEvents.map(renderEventCard)}
                      </div>
                    </section>
                  )}
                </>
              )}

              {/* Pagination */}
              {eventsPagination.has_next && (
                <div className="mt-8 flex items-center justify-center">
                  {eventsWaiting ? <Spinner size={6} /> : (
                    <Button
                      onClick={() => {
                        searchParams.set("page", (queryPageNumber + 1).toString());
                        setSearchParams(searchParams)
                      }}
                      width="fit"
                      bgColour="transparent hover:bg-dark-grey"
                      borderColour="dark-grey"
                      textColour="dark-grey hover:text-white"
                    >
                      Show more
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* You may also like */}
        <SectionHeader
          text="You may also like"
        />

        {/* Topics and collections */}
        <ExploreTopicsAndCollections />

      </div>

    </Template>
  );
}
